import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Nullable } from '@types'

import { SelectChangeEvent } from '@mui/material'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'

import { NewCountAssetsBadge, SearchResultsFilterSelect, SearchResultsFilterWrapper, StyledExpandMoreIcon } from './index.styles'
import { SelectOption } from './index.types'

export interface SelectWithLabelProps {
  label: string
  onSelect: (id: number, option?: SelectOption) => void
  selectedOptionId?: SelectOption['id']
  options: SelectOption[]
  disabled?: boolean
  renderSelectedState?: (selectedOption: SelectOption) => ReactNode
  renderOption?: (selectedOption: SelectOption) => ReactNode
  emptySelection?: boolean
  emptySelectionLabel?: Nullable<string>
  width?: string
  maxListHeight?: string
  containerId?: string
}

const SelectWithLabeledValue = ({
  containerId,
  label,
  onSelect,
  selectedOptionId,
  options: rawOptions,
  disabled,
  renderSelectedState,
  renderOption,
  emptySelection,
  emptySelectionLabel,
  width,
  maxListHeight,
}: SelectWithLabelProps) => {
  const { t } = useTranslation()

  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false)

  const handleOpenSelect = () => {
    setIsSelectOpen(true)
  }

  const handleCloseSelect = () => {
    setIsSelectOpen(false)
  }

  const options = useMemo(() => {
    if (emptySelection) {
      return [{ id: 0, label: emptySelectionLabel || label }, ...rawOptions]
    }

    return rawOptions
  }, [emptySelection, emptySelectionLabel, label, rawOptions])

  const selectedOption =
    useMemo(() => options.find(({ id }) => id === selectedOptionId), [options, selectedOptionId]) || options[0]

  const renderValue = useCallback(() => {
    if (renderSelectedState) {
      return renderSelectedState(selectedOption)
    }

    if (emptySelection && selectedOption?.id === 0) {
      return <Typography variant="body1">{t(label)}</Typography>
    }

    return <Typography variant="body1">{`${t(label)}: ${t(selectedOption?.label)}`}</Typography>
  }, [t, label, selectedOption, renderSelectedState, emptySelection])

  return (
    <SearchResultsFilterWrapper id={containerId}>
      <SearchResultsFilterSelect
        inputProps={{ 'data-testid': label }}
        disabled={disabled}
        IconComponent={StyledExpandMoreIcon}
        value={selectedOptionId}
        onChange={(event: SelectChangeEvent<unknown>) => {
          const elId = event?.target?.value as unknown as number
          onSelect(
            elId,
            options.find(({ id }) => elId === id),
          )
        }}
        onOpen={handleOpenSelect}
        onClose={handleCloseSelect}
        renderValue={renderValue}
        $open={isSelectOpen}
        $width={width}
        MenuProps={{ PaperProps: { style: { maxHeight: maxListHeight } } }}
      >
        {options.map(option =>
          renderOption ? (
            renderOption(option)
          ) : (
            <MenuItem key={option.id} value={option.id} disabled={option.id === selectedOptionId} divider={option.divider}>
              <Box display="flex" alignItems="center">
                <Typography variant="body1">{t(option.label)}</Typography>
                {option.badgeContent && <NewCountAssetsBadge>{option.badgeContent}</NewCountAssetsBadge>}
              </Box>
            </MenuItem>
          ),
        )}
      </SearchResultsFilterSelect>
    </SearchResultsFilterWrapper>
  )
}

export default SelectWithLabeledValue
